import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixStoresCatalogCatalogImportV3CatalogImportsUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/stores/v3/catalog-imports',
        destPath: '/v3/catalog-imports',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_stores_catalog-imports-v-3';

/**
 * Creates a CatalogImport for upsert product operations.
 *
 * Returns `catalogImport.id` that can be used in subsequent AddProductsToImport and SubmitCatalogImport calls.
 */
export function createCatalogImport(
  payload: object
): RequestOptionsFactory<any> {
  function __createCatalogImport({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'catalogImport.createdDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.catalog_import',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.catalog_import.v3.CatalogImports.CreateCatalogImport',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCatalogImportV3CatalogImportsUrl({
        protoPath: '/v3/catalog-imports',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'catalogImport.createdDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __createCatalogImport;
}

/**
 * Add products in bulk to an existing catalog import session.
 *
 * Products are stored in temporary storage until SubmitCatalogImport is called.
 * Can be called multiple times for the same import session.
 *
 * > **Notes:**
 * > + Products are upserted based on the `handle` field
 * > + Updates don't require passing the current revision number and overwrites existing fields which are specified in `product`
 * > + Pass `category_ids` to update categories assignments, overwrites existing assignments
 */
export function addProductsToImport(
  payload: object
): RequestOptionsFactory<any> {
  function __addProductsToImport({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'products.fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          { path: 'products.product.physicalProperties.pricePerUnit.quantity' },
          {
            path: 'products.product.physicalProperties.shippingWeightRange.minValue',
          },
          {
            path: 'products.product.physicalProperties.shippingWeightRange.maxValue',
          },
          {
            path: 'products.product.variantsInfo.variants.physicalProperties.pricePerUnit.settings.quantity',
          },
          {
            path: 'products.product.variantsInfo.variants.revenueDetails.profitMargin',
          },
          {
            path: 'products.product.subscriptionDetails.subscriptions.discount.percentOff',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'products.product.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'products.product.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'products.product.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'products.product.description.nodes.imageData.image.duration',
          },
          { path: 'products.product.description.nodes.imageData.crop.x' },
          { path: 'products.product.description.nodes.imageData.crop.y' },
          { path: 'products.product.description.nodes.imageData.crop.width' },
          { path: 'products.product.description.nodes.imageData.crop.height' },
          {
            path: 'products.product.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'products.product.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'products.product.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'products.product.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'products.product.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'products.product.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'products.product.description.nodes.videoData.video.duration',
          },
          {
            path: 'products.product.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'products.product.description.nodes.audioData.audio.duration',
          },
          {
            path: 'products.product.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'products.product.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'products.product.description.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'products.product.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'products.product.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'products.product.description.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'products.product.description.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'products.product.description.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          { path: 'products.product.description.nodes.tocData.fontSize' },
          { path: 'products.product.description.nodes.tocData.itemSpacing' },
          {
            path: 'products.product.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.background.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.background.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.background.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.buttonData.styles.backgroundHover.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.items.image.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.items.video.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.items.video.thumbnail.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.galleryData.options.item.ratio',
          },
          {
            path: 'products.product.infoSections.description.nodes.imageData.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.imageData.crop.x',
          },
          {
            path: 'products.product.infoSections.description.nodes.imageData.crop.y',
          },
          {
            path: 'products.product.infoSections.description.nodes.imageData.crop.width',
          },
          {
            path: 'products.product.infoSections.description.nodes.imageData.crop.height',
          },
          {
            path: 'products.product.infoSections.description.nodes.mapData.mapSettings.lat',
          },
          {
            path: 'products.product.infoSections.description.nodes.mapData.mapSettings.lng',
          },
          {
            path: 'products.product.infoSections.description.nodes.pollData.poll.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.pollData.poll.options.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.pollData.design.poll.background.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.appEmbedData.image.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.videoData.video.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.videoData.thumbnail.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.audioData.audio.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.audioData.coverImage.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backgroundImage.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdropImage.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdropImage.overlay.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdropImage.overlay.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdropImage.overlay.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.background.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.background.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.background.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdrop.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdrop.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.layoutData.backdrop.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.shapeData.shape.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.background.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.background.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.background.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.backgroundImage.media.duration',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.backgroundImage.overlay.gradient.centerX',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.backgroundImage.overlay.gradient.centerY',
          },
          {
            path: 'products.product.infoSections.description.nodes.cardData.backgroundImage.overlay.gradient.stops.position',
          },
          {
            path: 'products.product.infoSections.description.nodes.tocData.fontSize',
          },
          {
            path: 'products.product.infoSections.description.nodes.tocData.itemSpacing',
          },
          {
            path: 'products.product.infoSections.description.nodes.smartBlockCellData.shape.duration',
          },
          {
            path: 'products.product.variantsInfo.variants.physicalProperties.weight',
          },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'products.product.description.metadata.createdTimestamp' },
          { path: 'products.product.description.metadata.updatedTimestamp' },
          { path: 'products.product.media.main.image.urlExpirationDate' },
          { path: 'products.product.media.main.video.urlExpirationDate' },
          {
            path: 'products.product.media.main.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.media.main.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.media.main.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.image.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.media.itemsInfo.items.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.options.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.image.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.image.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.resolutions.poster.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.linkedMedia.video.posters.urlExpirationDate',
          },
          {
            path: 'products.product.modifiers.choicesSettings.choices.displayImage.urlExpirationDate',
          },
          {
            path: 'products.product.infoSections.description.metadata.createdTimestamp',
          },
          {
            path: 'products.product.infoSections.description.metadata.updatedTimestamp',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.catalog_import',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.catalog_import.v3.CatalogImports.AddProductsToImport',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCatalogImportV3CatalogImportsUrl({
        protoPath: '/v3/catalog-imports/{catalogImportId}/add-products',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __addProductsToImport;
}

/**
 * Submits all products in the catalog import for processing.
 * Once called, no more items can be added to this import session.
 */
export function submitCatalogImport(
  payload: object
): RequestOptionsFactory<any> {
  function __submitCatalogImport({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.stores.catalog.v3.catalog_import',
      method: 'POST' as any,
      methodFqn:
        'wix.stores.catalog.catalog_import.v3.CatalogImports.SubmitCatalogImport',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixStoresCatalogCatalogImportV3CatalogImportsUrl({
        protoPath: '/v3/catalog-imports/{catalogImportId}/submit',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __submitCatalogImport;
}
